export const AUTH_RETURN_TO_STORAGE_KEY = 'rely.auth.returnTo';
export const AUTH_PENDING_RETURN_TO_STORAGE_KEY =
  'rely.auth.pendingReturnTo';

function getLocationSnapshot(locationOverride) {
  if (locationOverride) {
    return locationOverride;
  }

  if (typeof window !== 'undefined' && window.location) {
    return window.location;
  }

  return {
    pathname: '/',
    search: '',
    hash: '',
  };
}

export function buildAuthReturnTo(locationOverride) {
  const location = getLocationSnapshot(locationOverride);
  return `${location.pathname || '/'}${location.search || ''}${
    location.hash || ''
  }`;
}

function getSessionStorage(storageOverride) {
  if (storageOverride) {
    return storageOverride;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
}

function sanitizeReturnTo(returnTo) {
  const hasControlCharacters =
    typeof returnTo === 'string' &&
    Array.from(returnTo).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    });

  if (
    typeof returnTo !== 'string' ||
    !returnTo.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.includes('\\') ||
    hasControlCharacters
  ) {
    return null;
  }

  return returnTo;
}

function persistReturnTo(returnTo, storageOverride) {
  const storage = getSessionStorage(storageOverride);

  try {
    storage?.setItem?.(AUTH_RETURN_TO_STORAGE_KEY, returnTo);
  } catch (_error) {
    // Auth0 appState remains the primary redirect source.
  }
}

function consumeStoredReturnTo(storageOverride) {
  const storage = getSessionStorage(storageOverride);
  let storedReturnTo = null;

  try {
    storedReturnTo = storage?.getItem?.(AUTH_RETURN_TO_STORAGE_KEY) || null;
    storage?.removeItem?.(AUTH_RETURN_TO_STORAGE_KEY);
  } catch (_error) {
    return null;
  }

  return sanitizeReturnTo(storedReturnTo);
}

function persistPendingReturnTo(returnTo, storageOverride) {
  const storage = getSessionStorage(storageOverride);

  try {
    storage?.setItem?.(AUTH_PENDING_RETURN_TO_STORAGE_KEY, returnTo);
  } catch (_error) {
    // The history update and popstate notification remain available as fallbacks.
  }
}

export function consumePendingAuthReturnTo(storageOverride) {
  const storage = getSessionStorage(storageOverride);
  let pendingReturnTo = null;

  try {
    pendingReturnTo =
      storage?.getItem?.(AUTH_PENDING_RETURN_TO_STORAGE_KEY) || null;
    storage?.removeItem?.(AUTH_PENDING_RETURN_TO_STORAGE_KEY);
  } catch (_error) {
    return null;
  }

  return sanitizeReturnTo(pendingReturnTo);
}

export function buildLoginRedirectOptions(locationOverride, options = {}) {
  const returnTo = sanitizeReturnTo(buildAuthReturnTo(locationOverride)) || '/';
  persistReturnTo(returnTo, options.sessionStorage);

  return {
    appState: {
      returnTo,
    },
  };
}

export function getAuth0CacheLocation(environment = process.env.NODE_ENV) {
  return environment === 'development' ? 'localstorage' : 'memory';
}

export function handleAuthRedirectCallback(appState, options = {}) {
  const history =
    options.history || (typeof window !== 'undefined' ? window.history : null);
  const documentRef =
    options.document || (typeof document !== 'undefined' ? document : null);
  const storedReturnTo = consumeStoredReturnTo(options.sessionStorage);
  const targetUrl =
    sanitizeReturnTo(appState?.returnTo) || storedReturnTo || '/';

  history?.replaceState?.({}, documentRef?.title || '', targetUrl);
  persistPendingReturnTo(targetUrl, options.sessionStorage);

  if (typeof window !== 'undefined' && typeof PopStateEvent !== 'undefined') {
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  return targetUrl;
}
